/**
 * History Section Components
 * POV-0052: Build History section UI (list + details + diff view)
 * POV-0053: Implement filter panel UI + session persistence
 * POV-0054: Implement grouped view by correlation_id
 * POV-0056/POV-0057: Export functionality with RBAC
 * 
 * Main components for the History accordion section in the Part Overview Drawer.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  History, Filter, ChevronDown, ChevronRight, 
  RefreshCw, Shield, ShieldCheck, ShieldAlert,
  User, X, ExternalLink, Layers, List,
  Download, FileText, FileSpreadsheet
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { 
  getPartAuditHistory, 
  verifyAuditIntegrity,
  exportAuditHistoryCSV,
  exportAuditHistoryPDF,
  downloadBlob,
  AUDIT_ACTIONS, 
  getActionDisplay, 
  formatTimestamp 
} from '../../services/auditService';
import { SECTION_REGISTRY } from './AccordionSection';
import { useAuth } from '../../contexts/AuthContext';

// ============================================================================
// History Section Main Component (POV-0052)
// ============================================================================

export const HistorySection = ({ partId, onViewRelatedHistory }) => {
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Auth context for RBAC (POV-0057)
  const { user } = useAuth();
  const canExport = user?.roles?.includes('admin') || user?.roles?.includes('quality_manager');
  
  // Filter state (POV-0053)
  const [filters, setFilters] = useState({
    sectionKey: null,
    action: null,
    dateFrom: null,
    dateTo: null,
    controlledOnly: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // View mode: 'grouped' or 'flat' (POV-0054)
  const [viewMode, setViewMode] = useState('flat');
  
  // Verification state (POV-0058)
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Export state (POV-0056/POV-0057)
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  
  // Selected event for details view
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Fetch audit history
  const fetchHistory = useCallback(async (page = 1) => {
    if (!partId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getPartAuditHistory(partId, {
        page,
        pageSize: pagination.pageSize,
        sectionKey: filters.sectionKey,
        action: filters.action,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        controlledOnly: filters.controlledOnly,
      });
      
      setEvents(result.items || []);
      setPagination({
        page: result.pagination.page,
        pageSize: result.pagination.page_size,
        total: result.pagination.total_items,
        totalPages: result.pagination.total_pages,
      });
    } catch (err) {
      console.error('Failed to fetch audit history:', err);
      setError(err.response?.data?.detail || 'Failed to load audit history');
    } finally {
      setIsLoading(false);
    }
  }, [partId, pagination.pageSize, filters]);

  // Initial load
  useEffect(() => {
    fetchHistory(1);
  }, [partId, filters, fetchHistory]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    fetchHistory(newPage);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      sectionKey: null,
      action: null,
      dateFrom: null,
      dateTo: null,
      controlledOnly: false,
    });
  };

  // Handle verification (POV-0058)
  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const result = await verifyAuditIntegrity(partId);
      setVerificationResult(result);
    } catch (err) {
      console.error('Verification failed:', err);
      setVerificationResult({
        verification_status: 'ERROR',
        error_message: err.response?.data?.detail || 'Verification failed',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle export (POV-0056/POV-0057)
  const handleExport = async (format) => {
    if (!canExport) {
      setExportError('You do not have permission to export audit history');
      return;
    }
    
    setIsExporting(true);
    setExportError(null);
    setShowExportMenu(false);
    
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      
      if (format === 'csv') {
        const blob = await exportAuditHistoryCSV(partId, filters);
        downloadBlob(blob, `audit_history_${partId}_${timestamp}.csv`);
      } else if (format === 'pdf') {
        const blob = await exportAuditHistoryPDF(partId, filters);
        // Open in new window for printing as PDF
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err.response?.data?.detail || 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Group events by correlation_id for grouped view
  const groupedEvents = useMemo(() => {
    if (viewMode !== 'grouped') return null;
    
    const groups = new Map();
    events.forEach(event => {
      const corrId = event.correlation_id;
      if (!groups.has(corrId)) {
        groups.set(corrId, {
          correlationId: corrId,
          events: [],
          timestamp: event.timestamp,
          primaryAction: event.action,
          actorName: event.actor_name,
          reasonText: event.reason_text,
        });
      }
      groups.get(corrId).events.push(event);
    });
    
    return Array.from(groups.values()).sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [events, viewMode]);

  // Check if any filters are active
  const hasActiveFilters = filters.sectionKey || filters.action || 
    filters.dateFrom || filters.dateTo || filters.controlledOnly;

  return (
    <div className="space-y-4" data-testid="history-section">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-slate-500" />
          <span className="font-medium text-slate-700">Audit Trail</span>
          <span className="text-sm text-slate-500">
            ({pagination.total} events)
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 rounded-md p-0.5">
            <button
              onClick={() => setViewMode('flat')}
              className={cn(
                "px-2 py-1 text-xs rounded transition-colors",
                viewMode === 'flat' 
                  ? "bg-white shadow text-slate-900" 
                  : "text-slate-500 hover:text-slate-700"
              )}
              title="Flat view"
              data-testid="view-mode-flat"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grouped')}
              className={cn(
                "px-2 py-1 text-xs rounded transition-colors",
                viewMode === 'grouped' 
                  ? "bg-white shadow text-slate-900" 
                  : "text-slate-500 hover:text-slate-700"
              )}
              title="Grouped by action"
              data-testid="view-mode-grouped"
            >
              <Layers className="w-4 h-4" />
            </button>
          </div>
          
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors",
              showFilters || hasActiveFilters
                ? "bg-cyan-50 text-cyan-700" 
                : "text-slate-600 hover:bg-slate-100"
            )}
            data-testid="toggle-filters-button"
          >
            <Filter className="w-4 h-4" />
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-cyan-500 rounded-full" />
            )}
          </button>
          
          {/* Export Button (POV-0056/POV-0057) */}
          {canExport && (
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={isExporting || events.length === 0}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors",
                  showExportMenu
                    ? "bg-cyan-50 text-cyan-700"
                    : "text-slate-600 hover:bg-slate-100",
                  (isExporting || events.length === 0) && "opacity-50 cursor-not-allowed"
                )}
                title="Export audit history"
                data-testid="export-button"
              >
                {isExporting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </button>
              
              {/* Export Menu Dropdown */}
              {showExportMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-10" data-testid="export-menu">
                  <div className="p-1">
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded transition-colors"
                      data-testid="export-csv-button"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded transition-colors"
                      data-testid="export-pdf-button"
                    >
                      <FileText className="w-4 h-4 text-red-600" />
                      Export as PDF
                    </button>
                  </div>
                  <div className="border-t border-slate-100 p-2">
                    <p className="text-xs text-slate-500">
                      Exports {pagination.total} events with current filters
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Refresh */}
          <button
            onClick={() => fetchHistory(pagination.page)}
            disabled={isLoading}
            className="p-1 text-slate-500 hover:text-slate-700 rounded transition-colors disabled:opacity-50"
            title="Refresh"
            data-testid="refresh-history-button"
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* Export Error */}
      {exportError && (
        <div className="p-2 bg-red-50 text-red-700 text-sm rounded-lg flex items-center justify-between">
          <span>{exportError}</span>
          <button onClick={() => setExportError(null)} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Integrity Status & Verify Button (POV-0058) */}
      <IntegrityIndicator 
        result={verificationResult}
        isVerifying={isVerifying}
        onVerify={handleVerify}
      />

      {/* Filter Panel (POV-0053) */}
      {showFilters && (
        <HistoryFilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          onClear={clearFilters}
        />
      )}

      {/* Error State */}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && events.length === 0 && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse p-3 bg-slate-50 rounded-lg">
              <div className="h-4 bg-slate-200 rounded w-1/4 mb-2" />
              <div className="h-3 bg-slate-100 rounded w-3/4" />
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && events.length === 0 && !error && (
        <div className="py-8 text-center text-slate-500">
          <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No audit events found</p>
          {hasActiveFilters && (
            <button 
              onClick={clearFilters}
              className="mt-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Events List */}
      {!isLoading && events.length > 0 && (
        <div className="space-y-2">
          {viewMode === 'grouped' && groupedEvents ? (
            // Grouped View (POV-0054)
            groupedEvents.map(group => (
              <CorrelationGroup
                key={group.correlationId}
                group={group}
                onSelectEvent={setSelectedEvent}
              />
            ))
          ) : (
            // Flat View
            events.map(event => (
              <AuditEventCard
                key={event.event_id}
                event={event}
                onClick={() => setSelectedEvent(event)}
              />
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
          <span className="text-sm text-slate-500">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1 || isLoading}
              className="px-3 py-1 text-sm bg-white border border-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              data-testid="prev-page-button"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || isLoading}
              className="px-3 py-1 text-sm bg-white border border-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              data-testid="next-page-button"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Integrity Indicator (POV-0058)
// ============================================================================

const IntegrityIndicator = ({ result, isVerifying, onVerify }) => {
  if (!result) {
    return (
      <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Shield className="w-4 h-4" />
          <span>Hash chain not verified</span>
        </div>
        <button
          onClick={onVerify}
          disabled={isVerifying}
          className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
          data-testid="verify-chain-button"
        >
          {isVerifying ? (
            <>
              <RefreshCw className="w-3 h-3 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              <ShieldCheck className="w-3 h-3" />
              Verify Now
            </>
          )}
        </button>
      </div>
    );
  }

  const isPass = result.verification_status === 'PASS';
  
  return (
    <div className={cn(
      "p-2 rounded-lg",
      isPass ? "bg-green-50" : "bg-red-50"
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isPass ? (
            <ShieldCheck className="w-4 h-4 text-green-600" />
          ) : (
            <ShieldAlert className="w-4 h-4 text-red-600" />
          )}
          <span className={cn("text-sm font-medium", isPass ? "text-green-700" : "text-red-700")}>
            {isPass ? 'Audit log integrity verified' : 'Integrity check failed'}
          </span>
        </div>
        <div className="text-xs text-slate-500">
          {result.events_verified}/{result.total_events} events verified
        </div>
      </div>
      {result.verified_at && (
        <div className="mt-1 text-xs text-slate-500">
          Last verified: {formatTimestamp(result.verified_at)}
        </div>
      )}
      {result.error_message && (
        <div className="mt-1 text-xs text-red-600">
          {result.error_message}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Filter Panel (POV-0053)
// ============================================================================

const HistoryFilterPanel = ({ filters, onFilterChange, onClear }) => {
  return (
    <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid="history-filter-panel">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Filters</span>
        <button
          onClick={onClear}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Clear all
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {/* Section Filter */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Section</label>
          <select
            value={filters.sectionKey || ''}
            onChange={(e) => onFilterChange('sectionKey', e.target.value || null)}
            className="w-full px-2 py-1.5 text-sm bg-white border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid="filter-section"
          >
            <option value="">All Sections</option>
            {SECTION_REGISTRY.map(section => (
              <option key={section.key} value={section.key}>
                {section.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Action Filter */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Event Type</label>
          <select
            value={filters.action || ''}
            onChange={(e) => onFilterChange('action', e.target.value || null)}
            className="w-full px-2 py-1.5 text-sm bg-white border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid="filter-action"
          >
            <option value="">All Types</option>
            {AUDIT_ACTIONS.map(action => (
              <option key={action.value} value={action.value}>
                {action.icon} {action.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Date From */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">From Date</label>
          <input
            type="date"
            value={filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : ''}
            onChange={(e) => onFilterChange('dateFrom', e.target.value ? new Date(e.target.value) : null)}
            className="w-full px-2 py-1.5 text-sm bg-white border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid="filter-date-from"
          />
        </div>
        
        {/* Date To */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">To Date</label>
          <input
            type="date"
            value={filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : ''}
            onChange={(e) => onFilterChange('dateTo', e.target.value ? new Date(e.target.value) : null)}
            className="w-full px-2 py-1.5 text-sm bg-white border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid="filter-date-to"
          />
        </div>
      </div>
      
      {/* Controlled Changes Toggle */}
      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
        <input
          type="checkbox"
          checked={filters.controlledOnly}
          onChange={(e) => onFilterChange('controlledOnly', e.target.checked)}
          className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
          data-testid="filter-controlled-only"
        />
        Only show controlled changes (with reason-for-change)
      </label>
    </div>
  );
};

// ============================================================================
// Audit Event Card
// ============================================================================

const AuditEventCard = ({ event, onClick }) => {
  const actionDisplay = getActionDisplay(event.action);
  
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition-all group"
      data-testid={`audit-event-${event.event_id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Timestamp */}
          <div className="text-xs text-slate-500 mb-1">
            {formatTimestamp(event.timestamp, { showFull: false })}
          </div>
          
          {/* Action & Section */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg" role="img" aria-label={actionDisplay.label}>
              {actionDisplay.icon}
            </span>
            <span className="font-medium text-slate-900">{actionDisplay.label}</span>
            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
              {event.section_label}
            </span>
          </div>
          
          {/* Entity */}
          <div className="text-sm text-slate-600">
            {event.entity_type}: <span className="font-mono">{event.entity_id}</span>
          </div>
          
          {/* Actor */}
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <User className="w-3 h-3" />
            {event.actor_name}
            {event.actor_role && (
              <span className="text-slate-400">({event.actor_role})</span>
            )}
          </div>
          
          {/* Reason (if present) */}
          {event.reason_text && (
            <div className="mt-2 text-sm text-slate-600 italic border-l-2 border-slate-200 pl-2">
              &ldquo;{event.reason_text}&rdquo;
            </div>
          )}
        </div>
        
        {/* View Details Arrow */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// Correlation Group (POV-0054)
// ============================================================================

const CorrelationGroup = ({ group, onSelectEvent }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const primaryAction = getActionDisplay(group.primaryAction);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`correlation-group-${group.correlationId}`}>
      {/* Group Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-white hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
          <span className="text-lg">{primaryAction.icon}</span>
          <div className="text-left">
            <div className="font-medium text-slate-900">
              {formatTimestamp(group.timestamp)} — {primaryAction.label}
            </div>
            <div className="text-xs text-slate-500">
              {group.events.length} related event{group.events.length > 1 ? 's' : ''} • by {group.actorName}
            </div>
          </div>
        </div>
        
        {group.reasonText && (
          <div className="text-xs text-slate-500 max-w-[200px] truncate" title={group.reasonText}>
            &ldquo;{group.reasonText}&rdquo;
          </div>
        )}
      </button>
      
      {/* Expanded Events */}
      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-2 space-y-1">
          {group.events.map(event => {
            const action = getActionDisplay(event.action);
            return (
              <button
                key={event.event_id}
                onClick={() => onSelectEvent(event)}
                className="w-full flex items-center gap-2 p-2 text-left text-sm bg-white rounded hover:bg-slate-100 transition-colors"
              >
                <span>{action.icon}</span>
                <span className="font-medium">{action.label}</span>
                <span className="text-slate-500">{event.entity_type}</span>
                <span className="text-slate-400 font-mono text-xs">{event.entity_id}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Event Details Modal (POV-0052 - Diff View)
// ============================================================================

const EventDetailsModal = ({ event, onClose }) => {
  if (!event) return null;
  
  const actionDisplay = getActionDisplay(event.action);
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[600px] md:max-h-[80vh] bg-white rounded-lg shadow-xl z-50 flex flex-col overflow-hidden" data-testid="event-details-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Event Details</h3>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Metadata */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Metadata</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-slate-500">Event ID:</span>
                <span className="ml-2 font-mono text-xs">{event.event_id}</span>
              </div>
              <div>
                <span className="text-slate-500">Correlation ID:</span>
                <span className="ml-2 font-mono text-xs">{event.correlation_id?.slice(0, 8)}...</span>
              </div>
              <div className="col-span-2">
                <span className="text-slate-500">Timestamp:</span>
                <span className="ml-2">{formatTimestamp(event.timestamp, { showFull: true })}</span>
              </div>
            </div>
          </div>
          
          {/* Actor */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Actor</h4>
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{event.actor_name}</span>
              {event.actor_role && (
                <span className="text-slate-500">({event.actor_role})</span>
              )}
              <span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{event.actor_type}</span>
            </div>
          </div>
          
          {/* Entity */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Entity</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-slate-500">Section:</span>
                <span className="ml-2">{event.section_label}</span>
              </div>
              <div>
                <span className="text-slate-500">Action:</span>
                <span className="ml-2">{actionDisplay.icon} {actionDisplay.label}</span>
              </div>
              <div>
                <span className="text-slate-500">Entity Type:</span>
                <span className="ml-2">{event.entity_type}</span>
              </div>
              <div>
                <span className="text-slate-500">Entity ID:</span>
                <span className="ml-2 font-mono text-xs">{event.entity_id}</span>
              </div>
            </div>
          </div>
          
          {/* Changes / Diff */}
          {event.changes && event.changes.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Changes</h4>
              <div className="bg-slate-50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-left">
                      <th className="px-3 py-2 font-medium text-slate-600">Field</th>
                      <th className="px-3 py-2 font-medium text-slate-600">Before</th>
                      <th className="px-3 py-2 font-medium text-slate-600">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.changes.map((change, idx) => (
                      <tr key={idx} className="border-t border-slate-200">
                        <td className="px-3 py-2 font-medium text-slate-700">{change.field}</td>
                        <td className="px-3 py-2 text-red-600">
                          {change.old_value === null || change.old_value === undefined 
                            ? <span className="text-slate-400">—</span>
                            : String(change.old_value)
                          }
                        </td>
                        <td className="px-3 py-2 text-green-600">
                          {change.new_value === null || change.new_value === undefined
                            ? <span className="text-slate-400">—</span>
                            : String(change.new_value)
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Reason for Change */}
          {(event.reason_code || event.reason_text) && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Reason for Change</h4>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                {event.reason_code && (
                  <div className="text-xs text-amber-600 mb-1">
                    Code: {event.reason_code}
                  </div>
                )}
                <div className="text-sm text-amber-800">
                  {event.reason_text || 'No reason provided'}
                </div>
              </div>
            </div>
          )}
          
          {/* Hash Info */}
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Hash Chain</h4>
            <div className="text-xs text-slate-500 font-mono space-y-1">
              <div>
                <span className="text-slate-400">Previous:</span> {event.previous_hash?.slice(0, 16)}...
              </div>
              <div>
                <span className="text-slate-400">Current:</span> {event.event_hash?.slice(0, 16)}...
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Related History Link (POV-0055)
// ============================================================================

export const ViewRelatedHistoryLink = ({ sectionKey, onClick }) => {
  return (
    <button
      onClick={() => onClick?.(sectionKey)}
      className="flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-700 transition-colors mt-3"
      data-testid={`view-related-history-${sectionKey}`}
    >
      <History className="w-3 h-3" />
      View related history
      <ExternalLink className="w-3 h-3" />
    </button>
  );
};

export default HistorySection;
